import { AutoAwesome, ExpandMore, Psychology, TrendingDown, TrendingFlat, TrendingUp } from "@mui/icons-material";
import { Accordion, AccordionDetails, AccordionSummary, Box, Button, Card, CardContent, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, Grid, IconButton, InputLabel, MenuItem, Paper, Select, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography } from "@mui/material";
import { BarChart as MuiBarChart } from "@mui/x-charts";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { token_keys } from "../../constants";
import ModernToggle from "../ModernToggle";

// Base URL for backend
const BACKEND_BASE_URL =
    process.env.REACT_APP_BACKEND_URL ||
    (process.env.NODE_ENV === 'development'
        ? 'http://localhost:8000'
        : null);

const getWebSocketUrl = (httpUrl) => {
    if (httpUrl.startsWith('https://')) return httpUrl.replace('https://', 'wss://');
    if (httpUrl.startsWith('http://')) return httpUrl.replace('http://', 'ws://');
    return httpUrl;
};

const CellInsightDialog = ({ open, onClose, reportId, query }) => {
    const intl = useIntl();
    const [response, setResponse] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const wsRef = useRef(null);

    const startStream = useCallback(() => {
        if (!reportId || !query) return;

        setResponse('');
        setError(null);
        setLoading(true);

        const accessToken = localStorage.getItem(token_keys.access_token);
        const wsBaseUrl = getWebSocketUrl(BACKEND_BASE_URL);
        const url = `${wsBaseUrl}/ws/rag/`;

        const ws = new WebSocket(url, ["Bearer", accessToken]);
        wsRef.current = ws;

        ws.onopen = () => {
            ws.send(JSON.stringify({
                user_input: query,
                history: [],
                report_ids: [reportId],
                channel: 'keywords_table_cell_info',
                session_id: crypto.randomUUID(),
                document_ids: [],
            }));
        };

        ws.onmessage = (event) => {
            try {
                const data = JSON.parse(event.data);
                if (data.done) {
                    ws.close();
                    setLoading(false);
                    return;
                }
                if (!data.message) return;
                const chunk = data.message
                    .split('\n\n')
                    .map(p => p.replace('data: ', ''))
                    .join('')
                    .replaceAll('$n$l$', '\n');
                setResponse(prev => prev + chunk);
            } catch (err) {
                console.error('Keyword CellInsight parse error:', err);
            }
        };

        ws.onerror = () => {
            setError(intl.formatMessage({ id: 'topicsSection.cell_insight_error', defaultMessage: 'Connection error. Please try again.' }));
            setLoading(false);
        };

        ws.onclose = (event) => {
            if (!event.wasClean && loading) {
                setError(intl.formatMessage({ id: 'topicsSection.cell_insight_lost', defaultMessage: 'Connection lost.' }));
                setLoading(false);
            }
        };
    }, [reportId, query, intl]);

    useEffect(() => {
        if (open && query) {
            startStream();
        }
        return () => {
            wsRef.current?.close();
        };
    }, [open, query]);

    const handleClose = () => {
        wsRef.current?.close();
        setResponse('');
        setError(null);
        setLoading(false);
        onClose();
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth disableScrollLock>
            <DialogTitle sx={{
                background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                py: 1.5,
            }}>
                <AutoAwesome fontSize="small" />
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    <FormattedMessage id="topicsSection.cell_insight" defaultMessage="Cell Insight" />
                </Typography>
            </DialogTitle>
            <DialogContent sx={{ pt: 3, minHeight: 150 }}>
                {error && (
                    <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>
                )}
                {loading && !response && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress size={28} />
                    </Box>
                )}
                {response && (
                    <Box sx={{
                        '& p': { mt: 0, mb: 1 },
                        '& ul, & ol': { pl: 2, mb: 1 },
                        fontSize: '0.9rem',
                        lineHeight: 1.6,
                        // GFM tables need both the plugin below to parse and these
                        // borders to be readable; wide ones scroll inside the dialog
                        overflowX: 'auto',
                        '& table': { width: '100%', borderCollapse: 'collapse', fontSize: 13, my: 1 },
                        '& th, & td': { border: '0.5px solid rgba(21,101,192,0.15)', padding: '6px 10px', textAlign: 'left' },
                        '& th': { background: 'rgba(21,101,192,0.06)', color: '#0d47a1', fontWeight: 500 },
                    }}>
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>{response}</ReactMarkdown>
                        {loading && <span style={{ opacity: 0.5 }}>▊</span>}
                    </Box>
                )}
            </DialogContent>
            <DialogActions sx={{ px: 2, pb: 2 }}>
                <Button onClick={handleClose} variant="outlined" size="small">
                    <FormattedMessage id="topicsSection.close" defaultMessage="Close" />
                </Button>
            </DialogActions>
        </Dialog>
    );
};

// Heatmap View Component
const HeatmapView = ({ keywordsData, selectedQuestion, setSelectedQuestion, sortedKeywords, reportId, onCellInsight }) => {
    const intl = useIntl();
    if (!selectedQuestion) {
        return (
            <Box sx={{
                p: 4,
                borderRadius: 3,
                background: 'rgba(255,255,255,0.95)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255,255,255,0.2)',
                textAlign: 'center'
            }}>
                <Typography variant="h6" sx={{ color: 'text.secondary', mb: 2 }}>
                    <FormattedMessage id="keywordsSection.select_question_title" defaultMessage="Select a question to view sentiment analysis" />
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    <FormattedMessage id="keywordsSection.select_question_desc" defaultMessage="Choose a question from the dropdown above to see how keywords relate to different response choices" />
                </Typography>
            </Box>
        );
    }

    // Find the selected question data
    const questionData = keywordsData.flatMap(keyword =>
        keyword.questions?.filter(q => q.text === selectedQuestion) || []
    )[0];

    if (!questionData) {
        return (
            <Box sx={{
                p: 4,
                borderRadius: 3,
                background: 'rgba(255,255,255,0.95)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255,255,255,0.2)',
                textAlign: 'center'
            }}>
                <Typography variant="h6" sx={{ color: 'error.main', mb: 2 }}>
                    <FormattedMessage id="keywordsSection.question_data_not_found" defaultMessage="Question data not found" />
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    <FormattedMessage id="keywordsSection.select_different_question" defaultMessage="Please select a different question" />
                </Typography>
            </Box>
        );
    }

    // Get all choices for this question
    const choices = questionData.choices || [];

    // Use all keywords (not filtered) - show all keywords as rows
    const allKeywords = keywordsData;

    // Get sentiment scores for each keyword-choice combination
    const getSentimentScore = (keyword, choiceText) => {
        const question = keyword.questions?.find(q => q.text === selectedQuestion);
        const choice = question?.choices?.find(c => c.text === choiceText);
        return choice?.sentiment || 0; // Return 0 for missing data
    };

    // Function to get radial gradient based on sentiment score
    const getSentimentGradient = (score) => {
        if (score === 0) return '#f8f9fa'; // Light gray for neutral/zero

        const intensity = Math.min(Math.abs(score), 1);
        const radius = Math.round(3 + (intensity * 97)); // 20% to 100% radius
        const radius2 = radius * 2;

        if (score > 0) {
            // Positive sentiment - green radial gradient
            return `radial-gradient(circle ${radius2}px at center, rgba(34, 197, 94, 0.8) 0%, rgba(34, 197, 94, 0.4) ${radius}%, #f8f9fa 100%)`;
        } else {
            // Negative sentiment - red radial gradient
            return `radial-gradient(circle ${radius2}px at center, rgba(220, 38, 38, 0.8) 0%, rgba(220, 38, 38, 0.4) ${radius}%, #f8f9fa 100%)`;
        }
    };

    // Function to get text color based on background
    const getTextColor = (score) => {
        if (score === 0) return '#666';
        if (score > 0) return '#444';
        // return '#aaa';
        const intensity = Math.min(Math.abs(score), 1);
        const radius = Math.round(3 + (intensity * 97)); // 20% to 100% radius
        const radius2 = radius * 2;
        return radius2 < 40 ? '#444' : 'white';
    };

    return (
        <Box>
            {/* Question Title */}
            <Box sx={{ mb: 3, p: 3, borderRadius: 3, background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.08) 0%, rgba(118, 75, 162, 0.08) 100%)', border: '1px solid rgba(102, 126, 234, 0.2)' }}>
                {/* Question Selector */}
                <FormControl size="small" sx={{ width: '100%' }}>
                    <InputLabel><FormattedMessage id="keywordsSection.select_different_question_label" defaultMessage="Select Different Question" /></InputLabel>
                    <Select
                        value={selectedQuestion || ""}
                        onChange={(e) => setSelectedQuestion(e.target.value)}
                        label={intl.formatMessage({ id: "keywordsSection.select_different_question_label", defaultMessage: "Select Different Question" })}
                    >
                        {keywordsData.flatMap(keyword =>
                            keyword.questions?.map(question => ({
                                value: question.text,
                                label: question.text,
                                question: question
                            })) || []
                        ).filter((item, index, arr) =>
                            arr.findIndex(other => other.value === item.value) === index
                        ).map((item, index) => (
                            <MenuItem key={index} value={item.value}>
                                <Tooltip title={item.value} placement="left">
                                    <Typography variant="body2" sx={{
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        maxWidth: 500
                                    }}>
                                        {item.label}
                                    </Typography>
                                </Tooltip>
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Box>

            {/* Heatmap Table */}
            <Box sx={{
                p: 3,
                borderRadius: 3,
                background: 'rgba(255,255,255,0.95)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255,255,255,0.2)',
                overflow: 'auto'
            }}>
                <Box sx={{ minWidth: 800 }}>
                    {/* Table Header */}
                    <Box sx={{ display: 'flex', mb: 2 }}>
                        <Box sx={{
                            width: 150,
                            p: 2.5,
                            fontWeight: 600,
                            background: 'linear-gradient(135deg,rgb(87, 108, 215) 0%,rgba(102, 126, 234, 0.27) 90%)',
                            color: 'white',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-start',
                            borderTopLeftRadius: 12,
                            boxShadow: '0 2px 8px rgba(102, 126, 234, 0.3)'
                        }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                <FormattedMessage id="keywordsSection.keywords" defaultMessage="Keywords" />
                            </Typography>
                        </Box>
                        {choices.map((choice, index) => (
                            <Box key={index} sx={{
                                flex: 1,
                                p: 2.5,
                                fontWeight: 600,
                                background: index === choices.length - 1 ?
                                    'linear-gradient(135deg,rgba(102, 126, 234, 0.27) 0%,rgb(255, 255, 255) 100%)'
                                    : 'rgba(102, 126, 234, 0.27)',
                                color: '#333',
                                textAlign: 'center',
                                borderTopRightRadius: index === choices.length - 1 ? 12 : 0,
                                borderTopLeftRadius: 0,
                                boxShadow: '0 2px 8px rgba(102, 126, 234, 0.3)'
                            }}>
                                <Tooltip title={choice.text} placement="top">
                                    <Typography variant="subtitle2" sx={{
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        display: 'block',
                                        fontWeight: 600
                                    }}>
                                        {choice.text}
                                    </Typography>
                                </Tooltip>
                            </Box>
                        ))}
                    </Box>

                    {/* Table Rows */}
                    {allKeywords.map((keyword, rowIndex) => (
                        <Box key={keyword.word} sx={{ display: 'flex', mb: 1 }}>
                            {/* Keyword Name */}
                            <Box sx={{
                                width: 150,
                                p: 2,
                                background: rowIndex % 2 === 0 ? '#f8f9fa' : 'white',
                                display: 'flex',
                                alignItems: 'center',
                                fontWeight: 'medium',
                                borderLeft: '1px solid #e0e0e0',
                                borderRight: '1px solid #e0e0e0'
                            }}>
                                <Typography variant="body2" sx={{
                                    fontWeight: 'bold',
                                    color: '#333'
                                }}>
                                    {keyword.word}
                                </Typography>
                            </Box>

                            {/* Sentiment Scores */}
                            {choices.map((choice, colIndex) => {
                                const score = getSentimentScore(keyword, choice.text);
                                const gradient = getSentimentGradient(score);
                                const textColor = getTextColor(score);

                                const otherChoices = choices
                                    .filter(otherChoice => otherChoice.text !== choice.text)
                                    .map(otherChoice => {
                                        const otherScore = getSentimentScore(keyword, otherChoice.text);
                                        return {
                                            groupName: otherChoice.text,
                                            sentiment: Number(otherScore.toFixed(3))
                                        };
                                    })
                                    .sort((a, b) => b.sentiment - a.sentiment);

                                return (
                                    <Tooltip
                                        key={colIndex}
                                        title={
                                            <Box>
                                                <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                                                    {keyword.word} - {choice.text}
                                                </Typography>
                                                <Typography variant="body2">
                                                    {intl.formatMessage({ id: "keywordsSection.sentiment_score", defaultMessage: "Sentiment Score: {score}" }, { score: score.toFixed(3) })}
                                                </Typography>
                                                <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>
                                                    {score > 0.1 ? intl.formatMessage({ id: "keywordsSection.positive", defaultMessage: "Positive" }) : score < -0.1 ? intl.formatMessage({ id: "keywordsSection.negative", defaultMessage: "Negative" }) : intl.formatMessage({ id: "keywordsSection.neutral", defaultMessage: "Neutral" })}
                                                </Typography>
                                            </Box>
                                        }
                                        arrow
                                    >
                                        <Box sx={{
                                            flex: 1,
                                            p: 2,
                                            textAlign: 'center',
                                            background: gradient,
                                            color: textColor,
                                            cursor: 'pointer',
                                            transition: 'all 0.2s ease',
                                            borderRight: colIndex === choices.length - 1 ? '1px solid #e0e0e0' : '1px solid #e0e0e0',
                                            '&:hover': {
                                                transform: 'scale(1.05)',
                                                boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
                                                zIndex: 1,
                                                position: 'relative'
                                            }
                                        }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.25 }}>
                                                <Typography variant="body2" sx={{
                                                    fontWeight: 'bold',
                                                    fontSize: '0.875rem'
                                                }}>
                                                    {score.toFixed(2)}
                                                </Typography>
                                                {reportId && (
                                                    <Tooltip title={intl.formatMessage({ id: "topicsSection.ask_why", defaultMessage: "Ask why" })}>
                                                        <IconButton
                                                            size="small"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                onCellInsight({
                                                                    questionTitle: selectedQuestion,
                                                                    keyword: keyword.word,
                                                                    groupName: choice.text,
                                                                    medianSentiment: score.toFixed(3),
                                                                    otherGroupComparisons: otherChoices,
                                                                });
                                                            }}
                                                            sx={{
                                                                p: 0.25,
                                                                color: score > 0.1 ? "#2e7d32" : score < -0.1 ? "#c62828" : "text.disabled",
                                                                '&:hover': {
                                                                    bgcolor: score > 0.1 ? 'rgba(46, 125, 50, 0.12)' : score < -0.1 ? 'rgba(198, 40, 40, 0.12)' : 'rgba(0,0,0,0.04)',
                                                                },
                                                            }}
                                                        >
                                                            <AutoAwesome sx={{ fontSize: '0.75rem' }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                            </Box>
                                        </Box>
                                    </Tooltip>
                                );
                            })}
                        </Box>
                    ))}
                </Box>
            </Box>

            {/* Legend */}
        </Box>
    );
};

// Sentiment Analysis View Component
const SentimentAnalysisView = ({ keywordsData, statsData, selectedSentimentQuestion, setSelectedSentimentQuestion, reportId, onCellInsight }) => {
    const intl = useIntl();
    // Sort keywords by frequency for better visual hierarchy
    const sortedKeywords = [...keywordsData].sort((a, b) => b.frequency - a.frequency);

    // Function to get sentiment data for selected question
    const getSentimentTableData = () => {
        if (!selectedSentimentQuestion || !selectedSentimentQuestion.choices) {
            return null;
        }

        const choices = selectedSentimentQuestion.choices.map(choice => choice.text);

        return sortedKeywords.map(keyword => {
            const rowData = { keyword: keyword.word };

            choices.forEach(choice => {
                // Look for exact match first
                let sentiment = keyword.attr_sentiment?.[choice];

                // If not found, look for combinations containing this choice
                if (sentiment === undefined) {
                    const combinations = Object.keys(keyword.attr_sentiment || {}).filter(key =>
                        key.includes(choice)
                    );

                    if (combinations.length > 0) {
                        // For now, take the first combination found - could be enhanced to average multiple
                        sentiment = keyword.attr_sentiment[combinations[0]];
                    }
                }

                rowData[choice] = sentiment !== undefined ? sentiment : null;
            });

            return rowData;
        });
    };

    const sentimentTableData = getSentimentTableData() || [];
    const keywordFrequencyByWord = new Map(sortedKeywords.map(keyword => [keyword.word, keyword.frequency || 0]));

    return (
        <Box>
            <Card>
                <CardContent sx={{ position: 'relative', zIndex: 1 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                        <Typography variant="h6" sx={{
                            // fontWeight: 'bold',
                            color: 'black',
                            mt: 1
                        }}>
                            <FormattedMessage id="keywordsSection.sentiment_analysis" defaultMessage="Sentiment Analysis" />
                        </Typography>
                    </Box>

                    {/* Question Selector - Horizontal Buttons */}
                    <Box sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 1,
                        mb: 3,
                        justifyContent: 'center'
                    }}>
                        {statsData.questions.map((question, index) => (
                            <Button
                                key={index}
                                variant={selectedSentimentQuestion === question ? "contained" : "outlined"}
                                onClick={() => setSelectedSentimentQuestion(question)}
                                sx={{
                                    backgroundColor: selectedSentimentQuestion === question ? '#1a365d' : 'transparent',
                                    borderColor: '#1a365d',
                                    color: selectedSentimentQuestion === question ? '#ffffff' : '#1a365d',
                                    fontSize: '0.75rem',
                                    fontWeight: 500,
                                    textTransform: 'none',
                                    px: 2,
                                    py: 0.75,
                                    borderRadius: 2,
                                    minWidth: 'auto',
                                    '&:hover': {
                                        backgroundColor: selectedSentimentQuestion === question ? '#0f2440' : 'rgba(26, 54, 93, 0.08)',
                                        borderColor: '#1a365d',
                                    },
                                    transition: 'all 0.2s ease'
                                }}
                            >
                                {question.title.length > 60 ? question.title.substring(0, 60) + "..." : question.title}
                            </Button>
                        ))}
                    </Box>

                    {/* Sentiment Table */}
                    {selectedSentimentQuestion && (
                        <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
                            <Table stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                                            <FormattedMessage id="keywordsSection.keyword" defaultMessage="Keyword" />
                                        </TableCell>
                                        {selectedSentimentQuestion.choices?.map((choice, index) => (
                                            <TableCell key={index} align="center" sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                                                {choice.text}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {sentimentTableData.map((row, rowIndex) => (
                                        <TableRow key={rowIndex} hover>
                                            <TableCell sx={{ fontWeight: 'medium' }}>
                                                {row.keyword}
                                            </TableCell>
                                            {selectedSentimentQuestion.choices?.map((choice, colIndex) => {
                                                const sentiment = row[choice.text];
                                                const otherGroupComparisons = (selectedSentimentQuestion.choices || [])
                                                    .filter(otherChoice => otherChoice.text !== choice.text)
                                                    .map(otherChoice => ({
                                                        groupName: otherChoice.text,
                                                        sentiment: Number(((row[otherChoice.text] ?? 0)).toFixed(3))
                                                    }))
                                                    .sort((a, b) => b.sentiment - a.sentiment);
                                                const getSentimentColor = (value) => {
                                                    if (value === null || value === undefined) return '#f5f5f5';
                                                    if (value > 0) return `rgba(76, 175, 80, ${Math.abs(value)})`;
                                                    if (value < 0) return `rgba(244, 67, 54, ${Math.abs(value)})`;
                                                    return '#e0e0e0';
                                                };

                                                return (
                                                    <TableCell key={colIndex} align="center" sx={{
                                                        backgroundColor: getSentimentColor(sentiment),
                                                        color: sentiment !== null && Math.abs(sentiment) > 0.5 ? '#fff' : '#333'
                                                    }}>
                                                        {sentiment !== null && sentiment !== undefined ? (
                                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                                                                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                                                    {(Math.abs(sentiment) * 100).toFixed(2)}%
                                                                </Typography>
                                                                {sentiment > 0.01 && <TrendingUp fontSize="small" />}
                                                                {sentiment < -0.01 && <TrendingDown fontSize="small" />}
                                                                {sentiment >= -0.01 && sentiment <= 0.01 && <TrendingFlat fontSize="small" />}
                                                                {reportId && (
                                                                    <Tooltip title={intl.formatMessage({ id: "topicsSection.ask_why", defaultMessage: "Ask why" })}>
                                                                        <IconButton
                                                                            size="small"
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                onCellInsight({
                                                                                    questionTitle: selectedSentimentQuestion?.title || selectedSentimentQuestion?.text || '',
                                                                                    keyword: row.keyword,
                                                                                    groupName: choice.text,
                                                                                    medianSentiment: Number(sentiment).toFixed(3),
                                                                                    otherGroupComparisons,
                                                                                    keywordFrequency: keywordFrequencyByWord.get(row.keyword) || 0,
                                                                                    keywordRowScores: (selectedSentimentQuestion.choices || []).map(choiceItem => ({
                                                                                        groupName: choiceItem.text,
                                                                                        sentiment: row[choiceItem.text]
                                                                                    })),
                                                                                    selectedGroupColumnScores: sentimentTableData.map(sentimentRow => ({
                                                                                        keyword: sentimentRow.keyword,
                                                                                        sentiment: sentimentRow[choice.text]
                                                                                    })),
                                                                                });
                                                                            }}
                                                                            sx={{
                                                                                p: 0.25,
                                                                                color: sentiment > 0.01 ? "#2e7d32" : sentiment < -0.01 ? "#c62828" : "text.disabled",
                                                                                '&:hover': {
                                                                                    bgcolor: sentiment > 0.01 ? 'rgba(46, 125, 50, 0.12)' : sentiment < -0.01 ? 'rgba(198, 40, 40, 0.12)' : 'rgba(0,0,0,0.04)',
                                                                                },
                                                                            }}
                                                                        >
                                                                            <AutoAwesome sx={{ fontSize: '0.75rem' }} />
                                                                        </IconButton>
                                                                    </Tooltip>
                                                                )}
                                                            </Box>
                                                        ) : (
                                                            <Typography variant="body2" sx={{ color: '#999' }}>
                                                                <FormattedMessage id="keywordsSection.na" defaultMessage="N/A" />
                                                            </Typography>
                                                        )}
                                                    </TableCell>
                                                );
                                            })}
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </CardContent>
            </Card>
        </Box>
    );
};

// Keywords Section Component
const KeywordsSection = ({ keywordsData, statsData, reportId, reportName }) => {
    const isSpanish = (reportName || '').toLowerCase().includes('spanish');
    const intl = useIntl();
    const [hoveredWord, setHoveredWord] = useState(null);
    const [selectedKeyword, setSelectedKeyword] = useState(null);
    const [viewMode, setViewMode] = useState("sentiment"); // "wordcloud", "barchart", or "heatmap"
    const [selectedQuestion, setSelectedQuestion] = useState(null);
    const [selectedSentimentQuestion, setSelectedSentimentQuestion] = useState(null);
    const [keywordDetailView, setKeywordDetailView] = useState("responses"); // "sentiment" or "responses"
    const [cellInsightOpen, setCellInsightOpen] = useState(false);
    const [cellInsightQuery, setCellInsightQuery] = useState('');

    const handleCellInsight = ({ questionTitle, keyword, groupName, medianSentiment, otherGroupComparisons, keywordFrequency, keywordRowScores, selectedGroupColumnScores }) => {
        const comparisonLines = isSpanish
            ? (otherGroupComparisons || [])
                .slice(0, 8)
                .map((item, index) => `${index + 1}. "${item.groupName}": sentimiento mediano ${item.sentiment > 0 ? "+" : ""}${item.sentiment.toFixed(3)}`)
                .join('\n')
            : (otherGroupComparisons || [])
                .slice(0, 8)
                .map((item, index) => `${index + 1}. "${item.groupName}": median sentiment ${item.sentiment > 0 ? "+" : ""}${item.sentiment.toFixed(3)}`)
                .join('\n');

        const keywordRowLines = (keywordRowScores || [])
            .map((item, index) => `${index + 1}. "${item.groupName}": ${item.sentiment === null || item.sentiment === undefined ? 'N/A' : `${item.sentiment > 0 ? "+" : ""}${Number(item.sentiment).toFixed(3)}`}`)
            .join('\n');

        const selectedGroupColumnLines = (selectedGroupColumnScores || [])
            .map((item, index) => `${index + 1}. "${item.keyword}": ${item.sentiment === null || item.sentiment === undefined ? 'N/A' : `${item.sentiment > 0 ? "+" : ""}${Number(item.sentiment).toFixed(3)}`}`)
            .join('\n');

        const query = isSpanish
            ? `Explica esta celda del mapa de calor de sentimientos de palabras clave en un lenguaje claro para una audiencia ejecutiva.

Contexto:
- Pregunta: "${questionTitle}"
- Palabra clave: "${keyword}"
- Frecuencia de la palabra clave: ${keywordFrequency ?? 0}
- Grupo / valor de atributo: "${groupName}"
- Valor de la celda: ${medianSentiment}

Cómo se define esta celda:
- Este número es el sentimiento mediano de todas las respuestas del grupo "${groupName}" que mencionan la palabra clave "${keyword}".
- No es un conteo ni un porcentaje; resume el tono de las respuestas de ese subgrupo sobre esta palabra clave.

Cómo se comparan otros grupos en esta misma palabra clave:
${comparisonLines || '- No había otros valores de sentimiento disponibles para esta palabra clave.'}

Contexto completo de la fila para la palabra clave "${keyword}" (todos los grupos de atributos para esta palabra clave):
${keywordRowLines || '- No hay contexto de fila disponible.'}

Contexto completo de la columna para el grupo "${groupName}" (este grupo en todas las palabras clave):
${selectedGroupColumnLines || '- No hay contexto de columna disponible.'}

Qué explicar:
- Qué dice este sentimiento mediano sobre cómo el grupo "${groupName}" percibe la palabra clave "${keyword}"
- Si es más positivo, más negativo o similar en comparación con otros grupos
- En qué posición se encuentra este grupo respecto al grupo con el sentimiento más positivo y al más negativo para esta palabra clave
- Qué debería inferir un líder y qué pregunta de seguimiento debería plantearse

Escribe la respuesta a nivel ejecutivo:
- sé directo
- sé útil
- evita el lenguaje técnico
- enfócate en el significado para el negocio, no en los detalles matemáticos; a menos que sean necesarios para la claridad`
            : `Explain this keyword sentiment heatmap cell in plain language for an executive audience.

Context:
- Question: "${questionTitle}"
- Keyword: "${keyword}"
- Keyword frequency: ${keywordFrequency ?? 0}
- Group / attribute value: "${groupName}"
- Cell value: ${medianSentiment}

How this cell is defined:
- This number is the median sentiment of all responses from group "${groupName}" that mention keyword "${keyword}".
- It is not a count or percentage; it summarizes the tone of that subgroup's responses about this keyword.

How other groups compare on this same keyword:
${comparisonLines || '- No other group sentiment values were available for this keyword.'}

Complete row context for keyword "${keyword}" (all attribute groups for this keyword):
${keywordRowLines || '- No row context available.'}

Complete column context for group "${groupName}" (this group across all keywords):
${selectedGroupColumnLines || '- No column context available.'}

What to explain:
- What this median sentiment says about how "${groupName}" feels about "${keyword}"
- Whether this is more positive, more negative, or similar versus other groups
- Where this group stands relative to the strongest positive and strongest negative groups for this keyword
- What a leader should infer and what follow-up question they should ask

Write the response at an executive level:
- be direct
- be helpful
- avoid technical jargon
- focus on the business meaning, not the math details; unless needed for clarity`;

        setCellInsightQuery(query);
        setCellInsightOpen(true);
    };

    // Convert top_terms object to keywords array if needed
    const processedKeywordsData = React.useMemo(() => {
        if (!keywordsData) return [];

        // If it's already an array, return as is
        if (Array.isArray(keywordsData)) return keywordsData;

        // If it's an object (top_terms format), convert to array
        if (typeof keywordsData === 'object') {
            return Object.entries(keywordsData).map(([word, termData]) => {
                // Replace null values with 0 in attr_sentiment
                const attr_sentiment = termData.attr_sentiment ?
                    Object.entries(termData.attr_sentiment).reduce((acc, [key, value]) => {
                        acc[key] = value === null ? 0 : value;
                        return acc;
                    }, {}) : {};

                return {
                    word,
                    frequency: termData.frequency || 0,
                    responses: termData.responses || [],
                    attr_sentiment,
                    // Add empty questions array for compatibility
                    questions: []
                };
            });
        }

        return [];
    }, [keywordsData]);

    // Auto-select first question when component mounts or keywordsData changes
    useEffect(() => {
        if (processedKeywordsData && processedKeywordsData.length > 0 && !selectedQuestion) {
            const firstQuestion = processedKeywordsData.flatMap(keyword =>
                keyword.questions?.map(question => question.text) || []
            ).find((question, index, arr) => arr.indexOf(question) === index);

            if (firstQuestion) {
                setSelectedQuestion(firstQuestion);
            }
        }
    }, [processedKeywordsData, selectedQuestion]);

    // Auto-select first sentiment question when component mounts or statsData changes
    useEffect(() => {
        if (statsData && statsData.questions && statsData.questions.length > 0 && !selectedSentimentQuestion) {
            const firstQuestion = statsData.questions[0];
            if (firstQuestion && firstQuestion.choices && firstQuestion.choices.length > 0) {
                setSelectedSentimentQuestion(firstQuestion);
            }
        }
    }, [statsData, selectedSentimentQuestion]);

    if (!processedKeywordsData || processedKeywordsData.length === 0) {
        return (
            <Card>
                <CardContent>
                    <Typography variant="h6" sx={{ mb: 2 }}>Keywords Analysis</Typography>
                    <Typography variant="body2" color="text.secondary">
                        <FormattedMessage id="keywordsSection.no_keywords_data" defaultMessage="No keywords data available" />
                    </Typography>
                </CardContent>
            </Card>
        );
    }

    // Sort keywords by frequency for better visual hierarchy
    const sortedKeywords = [...processedKeywordsData].sort((a, b) => b.frequency - a.frequency);

    // Calculate font sizes based on frequency with better scaling
    const maxFrequency = Math.max(...processedKeywordsData.map(k => k.frequency));
    const minFrequency = Math.min(...processedKeywordsData.map(k => k.frequency));

    const getFontSize = (frequency) => {
        const minSize = 14;
        const maxSize = 72;
        const normalized = (frequency - minFrequency) / (maxFrequency - minFrequency);
        // Use exponential scaling for better visual distinction
        const exponentialNormalized = Math.pow(normalized, 0.7);
        return minSize + exponentialNormalized * (maxSize - minSize);
    };


    const baseColors = [
        '#9C27B0', '#7B1FA2', '#6A1B9A', '#BAA0DA',
        '#3F51B5', '#303F9F', '#1976D2', '#94BAE5',
        '#FF8F00', '#49B2A6', '#E8D15E', '#8D6E63',
        '#FFAB40', '#CDB70E', '#F3A9D6', '#DAA997'
    ];

    // Shuffle colors randomly for variety
    const colors = [...baseColors].sort(() => Math.random() - 0.5);

    const getColor = (index) => colors[index % colors.length];


    const handleWordClick = (keyword) => {
        setSelectedKeyword(selectedKeyword?.word === keyword.word ? null : keyword);
    };

    return (
        <Box>
            <Card>
                <CardContent sx={{ position: 'relative', zIndex: 1 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                        <Typography variant="h6" sx={{
                            // fontWeight: 'bold',
                            color: 'black',
                            mt: 1
                        }}>
                            <FormattedMessage id="keywordsSection.keywords_analysis" defaultMessage="Keywords Analysis" />
                        </Typography>
                        <ModernToggle
                            leftLabel={intl.formatMessage({ id: "keywordsSection.word_cloud", defaultMessage: "Word Cloud" })}
                            rightLabel={intl.formatMessage({ id: "keywordsSection.sentiment_analysis", defaultMessage: "Sentiment Analysis" })}
                            checked={viewMode === "sentiment"}
                            onChange={(checked) => setViewMode(checked ? "sentiment" : "wordcloud")}
                        />
                    </Box>

                    {viewMode === "wordcloud" ? (
                        <Box>
                            {/* Enhanced Word Cloud */}
                            <Box
                                sx={{
                                    position: 'relative',
                                    // minHeight: 500,
                                    p: 4,
                                    borderRadius: 3,
                                    background: 'rgba(255,255,255,0.95)',
                                    backdropFilter: 'blur(10px)',
                                    border: '1px solid rgba(255,255,255,0.2)',
                                    overflow: 'hidden',
                                    '&::before': {
                                        content: '""',
                                        position: 'absolute',
                                        top: -50,
                                        left: -50,
                                        width: 100,
                                        height: 100,
                                        background: 'radial-gradient(circle, rgba(102,126,234,0.1) 0%, transparent 70%)',
                                        borderRadius: '50%',
                                        animation: 'float 6s ease-in-out infinite'
                                    },
                                    '&::after': {
                                        content: '""',
                                        position: 'absolute',
                                        bottom: -30,
                                        right: -30,
                                        width: 80,
                                        height: 80,
                                        background: 'radial-gradient(circle, rgba(118,75,162,0.1) 0%, transparent 70%)',
                                        borderRadius: '50%',
                                        animation: 'float 8s ease-in-out infinite reverse'
                                    },
                                    '@keyframes float': {
                                        '0%, 100%': { transform: 'translateY(0px) rotate(0deg)' },
                                        '50%': { transform: 'translateY(-20px) rotate(180deg)' }
                                    }
                                }}
                            >
                                {/* Word Cloud Container */}
                                <Box
                                    sx={{
                                        display: "flex",
                                        flexWrap: "wrap",
                                        gap: { xs: 1.5, md: 2.5 },
                                        justifyContent: "center",
                                        alignItems: "center",
                                        // minHeight: 400,
                                        position: 'relative',
                                        zIndex: 2
                                    }}
                                >
                                    {sortedKeywords.map((keyword, index) => {
                                        const fontSize = getFontSize(keyword.frequency);
                                        const color = getColor(index);
                                        const isHovered = hoveredWord === keyword.word;
                                        const isSelected = selectedKeyword?.word === keyword.word;

                                        return (
                                            <Tooltip
                                                key={index}
                                                title={
                                                    <Box sx={{ textAlign: 'center' }}>
                                                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                                                            {keyword.word}
                                                        </Typography>
                                                        <Typography variant="caption">
                                                            {intl.formatMessage({ id: "keywordsSection.frequency", defaultMessage: "Frequency: {count}" }, { count: keyword.frequency })}
                                                        </Typography>
                                                        {keyword.questions && keyword.questions.length > 0 && (
                                                            <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                                                                {intl.formatMessage({ id: "keywordsSection.related_questions", defaultMessage: "{count, plural, one {# related question} other {# related questions}}" }, { count: keyword.questions.length })}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                }
                                                arrow
                                                placement="top"
                                                open={isHovered}
                                                componentsProps={{
                                                    tooltip: {
                                                        sx: {
                                                            bgcolor: 'rgba(0,0,0,0.9)',
                                                            backdropFilter: 'blur(10px)',
                                                            border: '1px solid rgba(255,255,255,0.1)'
                                                        }
                                                    }
                                                }}
                                            >
                                                <Box
                                                    onMouseEnter={() => setHoveredWord(keyword.word)}
                                                    onMouseLeave={() => setHoveredWord(null)}
                                                    onClick={() => handleWordClick(keyword)}
                                                    sx={{
                                                        fontSize: `${fontSize}px`,
                                                        fontWeight: "900",
                                                        color: isSelected ? '#1976d2' : color,
                                                        cursor: "pointer",
                                                        transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                                                        userSelect: "none",
                                                        position: 'relative',
                                                        textShadow: isHovered ? '0 0 20px rgba(0,0,0,0.3)' : 'none',
                                                        transform: isHovered ? 'scale(1.15) rotate(2deg)' : 'scale(1) rotate(0deg)',
                                                        zIndex: isHovered ? 10 : 1,
                                                        border: isSelected ? '3px solid #1976d2' : 'none',
                                                        borderRadius: isSelected ? '8px' : 'none',
                                                        padding: isSelected ? '4px 8px' : '0',
                                                        backgroundColor: isSelected ? 'rgba(25, 118, 210, 0.1)' : 'transparent',
                                                        '&:hover': {
                                                            color: isSelected ? '#1976d2' : color,
                                                            filter: 'brightness(1.1) saturate(1.2)'
                                                        },
                                                        // Add subtle animation on load
                                                        animation: `fadeInUp 0.6s ease-out ${index * 0.1}s both`,
                                                        '@keyframes fadeInUp': {
                                                            '0%': {
                                                                opacity: 0,
                                                                transform: 'translateY(30px) scale(0.8)'
                                                            },
                                                            '100%': {
                                                                opacity: 1,
                                                                transform: 'translateY(0) scale(1)'
                                                            }
                                                        }
                                                    }}
                                                >
                                                    {keyword.word}
                                                </Box>
                                            </Tooltip>
                                        );
                                    })}
                                </Box>
                            </Box>
                            {/* Selected Keyword Details */}
                            {selectedKeyword && (
                                <Box sx={{ mt: 3 }}>
                                    <Card sx={{
                                        background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
                                        border: '2px solid #1976d2',
                                        boxShadow: '0 8px 32px rgba(25, 118, 210, 0.1)'
                                    }}>
                                        <CardContent>
                                            {/* Overview Stats */}
                                            <Grid container spacing={3} sx={{ mb: 3 }}>
                                                <Grid item xs={12} md={4}>
                                                    <Box sx={{
                                                        textAlign: 'center',
                                                        p: 2,
                                                        borderRadius: 2,
                                                        background: 'rgba(25, 118, 210, 0.05)',
                                                        border: '1px solid rgba(25, 118, 210, 0.1)'
                                                    }}>
                                                        <Typography variant="h4" sx={{ color: '#1976d2', fontWeight: 'bold' }}>
                                                            {selectedKeyword.frequency}
                                                        </Typography>
                                                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                                            <FormattedMessage id="keywordsSection.total_frequency" defaultMessage="Total Frequency" />
                                                        </Typography>
                                                    </Box>
                                                </Grid>
                                                <Grid item xs={12} md={4} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                    <ModernToggle
                                                        leftLabel={intl.formatMessage({ id: "keywordsSection.sentiment", defaultMessage: "Sentiment" })}
                                                        rightLabel={intl.formatMessage({ id: "keywordsSection.responses", defaultMessage: "Responses" })}
                                                        checked={keywordDetailView === "responses"}
                                                        onChange={(checked) => setKeywordDetailView(checked ? "responses" : "sentiment")}
                                                        size="small"
                                                    />
                                                </Grid>
                                                <Grid item xs={12} md={4}>
                                                    <Box sx={{
                                                        textAlign: 'center',
                                                        p: 2,
                                                        borderRadius: 2,
                                                        background: keywordDetailView === "sentiment" ? 'rgba(76, 175, 80, 0.05)' : 'rgba(255, 152, 0, 0.05)',
                                                        border: keywordDetailView === "sentiment" ? '1px solid rgba(76, 175, 80, 0.1)' : '1px solid rgba(255, 152, 0, 0.1)',
                                                        transition: 'all 0.3s ease'
                                                    }}>
                                                        <Typography variant="h4" sx={{
                                                            color: keywordDetailView === "sentiment" ? '#4caf50' : '#ff9800',
                                                            fontWeight: 'bold',
                                                            transition: 'color 0.3s ease'
                                                        }}>
                                                            {keywordDetailView === "sentiment"
                                                                ? Object.keys(selectedKeyword.attr_sentiment || {}).length
                                                                : (selectedKeyword.responses?.length || 0)
                                                            }
                                                        </Typography>
                                                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                                            {keywordDetailView === "sentiment" ? <FormattedMessage id="keywordsSection.filter_items" defaultMessage="Filter Items" /> : <FormattedMessage id="keywordsSection.sample_responses" defaultMessage="Sample Responses" />}
                                                        </Typography>
                                                    </Box>
                                                </Grid>
                                            </Grid>


                                            {/* Sentiment View - Compact Design */}
                                            {keywordDetailView === "sentiment" && selectedKeyword.attr_sentiment && Object.keys(selectedKeyword.attr_sentiment).length > 0 && (
                                                <Box sx={{
                                                    border: '1px solid #e2e8f0',
                                                    borderRadius: 2,
                                                    background: '#fff',
                                                    overflow: 'hidden',
                                                    maxWidth: 1020,
                                                    mx: 'auto'
                                                }}>
                                                    {/* Header */}
                                                    <Box sx={{
                                                        px: 2,
                                                        py: 1,
                                                        bgcolor: '#f8fafc',
                                                        borderBottom: '1px solid #e2e8f0',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 1
                                                    }}>
                                                        <Psychology sx={{ fontSize: 16, color: '#1976d2' }} />
                                                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1976d2' }}>
                                                            <FormattedMessage id="keywordsSection.sentiment_by_choice" defaultMessage="Sentiment by Choice" />
                                                        </Typography>
                                                    </Box>
                                                    {/* Compact rows - organized by question */}
                                                    <Box sx={{ maxHeight: 320, overflowY: 'auto' }}>
                                                        {(() => {
                                                            // Group choices by questions from statsData
                                                            const questions = statsData?.questions || [];
                                                            const attrSentiment = selectedKeyword.attr_sentiment;

                                                            // Create a map of choice text to sentiment value
                                                            const sentimentMap = new Map(
                                                                Object.entries(attrSentiment).filter(([choice]) => choice && choice.trim() !== '')
                                                            );

                                                            // Track which choices have been displayed
                                                            const displayedChoices = new Set();

                                                            // Group choices by their parent question
                                                            const questionGroups = questions.map(question => {
                                                                const questionChoices = (question.choices || [])
                                                                    .filter(choice => {
                                                                        const choiceText = choice.text;
                                                                        return choiceText && sentimentMap.has(choiceText) && !displayedChoices.has(choiceText);
                                                                    })
                                                                    .map(choice => {
                                                                        displayedChoices.add(choice.text);
                                                                        return {
                                                                            text: choice.text,
                                                                            sentiment: sentimentMap.get(choice.text)
                                                                        };
                                                                    });

                                                                return {
                                                                    title: question.title,
                                                                    choices: questionChoices
                                                                };
                                                            }).filter(group => group.choices.length > 0);

                                                            // Add any remaining choices that weren't matched to a question
                                                            const unmatchedChoices = Array.from(sentimentMap.entries())
                                                                .filter(([choice]) => !displayedChoices.has(choice))
                                                                .map(([text, sentiment]) => ({ text, sentiment }));

                                                            if (unmatchedChoices.length > 0) {
                                                                questionGroups.push({
                                                                    title: intl.formatMessage({ id: "keywordsSection.other", defaultMessage: "Other" }),
                                                                    choices: unmatchedChoices
                                                                });
                                                            }

                                                            let globalIndex = 0;

                                                            return questionGroups.map((group, groupIndex) => (
                                                                <Box key={groupIndex}>
                                                                    {/* Question header */}
                                                                    <Box sx={{
                                                                        px: 1.5,
                                                                        py: 0.5,
                                                                        bgcolor: '#e8f4fc',
                                                                        borderBottom: '1px solid #d0e8f5',
                                                                        borderTop: groupIndex > 0 ? '1px solid #d0e8f5' : 'none'
                                                                    }}>
                                                                        <Typography
                                                                            sx={{
                                                                                fontSize: '0.7rem',
                                                                                fontWeight: 600,
                                                                                color: '#1565c0',
                                                                                whiteSpace: 'nowrap',
                                                                                overflow: 'hidden',
                                                                                textOverflow: 'ellipsis'
                                                                            }}
                                                                            title={group.title}
                                                                        >
                                                                            {group.title}
                                                                        </Typography>
                                                                    </Box>
                                                                    {/* Choices for this question */}
                                                                    {group.choices.map((choiceData, choiceIndex) => {
                                                                        const sentiment = Number(choiceData.sentiment) || 0;
                                                                        const roundedPercent = (sentiment * 100).toFixed(2);
                                                                        const isZero = parseFloat(roundedPercent) === 0;
                                                                        const color = isZero ? "#9e9e9e" : sentiment > 0 ? "#4caf50" : "#f44336";
                                                                        const barWidth = Math.abs(sentiment) * 50;
                                                                        const finalBarWidth = isZero ? 0 : Math.max(barWidth, 2);
                                                                        const currentIndex = globalIndex++;

                                                                        return (
                                                                            <Box
                                                                                key={choiceIndex}
                                                                                sx={{
                                                                                    display: 'flex',
                                                                                    alignItems: 'center',
                                                                                    px: 1.5,
                                                                                    py: 0.5,
                                                                                    borderBottom: choiceIndex < group.choices.length - 1 ? '1px solid #f0f0f0' : 'none',
                                                                                    bgcolor: currentIndex % 2 === 0 ? '#fff' : '#fafafa',
                                                                                    gap: 1.5,
                                                                                    minHeight: 32
                                                                                }}
                                                                            >
                                                                                {/* Choice name */}
                                                                                <Typography
                                                                                    sx={{
                                                                                        fontSize: '0.75rem',
                                                                                        fontWeight: 500,
                                                                                        color: '#333',
                                                                                        width: 100,
                                                                                        flexShrink: 0,
                                                                                        whiteSpace: 'nowrap',
                                                                                        overflow: 'hidden',
                                                                                        textOverflow: 'ellipsis'
                                                                                    }}
                                                                                    title={choiceData.text}
                                                                                >
                                                                                    {choiceData.text}
                                                                                </Typography>
                                                                                {/* Mini progress bar */}
                                                                                <Box sx={{
                                                                                    flex: 1,
                                                                                    height: 10,
                                                                                    bgcolor: '#f0f0f0',
                                                                                    borderRadius: 0.5,
                                                                                    position: 'relative',
                                                                                    overflow: 'hidden'
                                                                                }}>
                                                                                    {/* Colored bar */}
                                                                                    {finalBarWidth > 0 && (
                                                                                        <Box sx={{
                                                                                            position: 'absolute',
                                                                                            left: '50%',
                                                                                            width: `${finalBarWidth}%`,
                                                                                            height: '100%',
                                                                                            bgcolor: color,
                                                                                            transform: sentiment < 0 ? 'translateX(-100%)' : 'none',
                                                                                            transition: 'width 0.2s ease',
                                                                                            zIndex: 1,
                                                                                            borderRadius: sentiment < 0 ? '0 0.5px 0.5px 0' : '0.5px 0 0 0.5px'
                                                                                        }} />
                                                                                    )}
                                                                                    {/* Black center line at 0% */}
                                                                                    <Box sx={{
                                                                                        position: 'absolute',
                                                                                        left: '50%',
                                                                                        width: 2,
                                                                                        height: '100%',
                                                                                        bgcolor: '#000',
                                                                                        transform: 'translateX(-50%)',
                                                                                        zIndex: 2
                                                                                    }} />
                                                                                </Box>
                                                                                {/* Percentage */}
                                                                                <Typography
                                                                                    sx={{
                                                                                        fontSize: '0.7rem',
                                                                                        fontWeight: 600,
                                                                                        color: color,
                                                                                        width: 50,
                                                                                        textAlign: 'right',
                                                                                        flexShrink: 0
                                                                                    }}
                                                                                >
                                                                                    {sentiment > 0 && !isZero ? "+" : ""}{roundedPercent}%
                                                                                </Typography>
                                                                            </Box>
                                                                        );
                                                                    })}
                                                                </Box>
                                                            ));
                                                        })()}
                                                    </Box>
                                                </Box>
                                            )}

                                            {/* Scrollable Responses */}
                                            {keywordDetailView === "responses" && selectedKeyword.responses && selectedKeyword.responses.length > 0 && (
                                                <Box>
                                                    <Typography variant="h6" sx={{
                                                        fontWeight: 'bold',
                                                        mb: 2,
                                                        color: '#1976d2',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        gap: 1,
                                                        textAlign: 'center',
                                                    }}>
                                                        <Psychology fontSize="small" />
                                                        <FormattedMessage id="keywordsSection.sample_responses" defaultMessage="Sample Responses" />
                                                    </Typography>
                                                    <Box sx={{
                                                        maxWidth: 1020,
                                                        mx: 'auto',
                                                        maxHeight: 300,
                                                        overflowY: 'auto',
                                                        border: '1px solid #e2e8f0',
                                                        borderRadius: 2,
                                                        background: '#fafafa',
                                                        '&::-webkit-scrollbar': {
                                                            width: '8px',
                                                        },
                                                        '&::-webkit-scrollbar-track': {
                                                            background: '#f1f1f1',
                                                            borderRadius: '4px',
                                                        },
                                                        '&::-webkit-scrollbar-thumb': {
                                                            background: '#1976d2',
                                                            borderRadius: '4px',
                                                            '&:hover': {
                                                                background: '#1565c0',
                                                            }
                                                        }
                                                    }}>
                                                        {selectedKeyword.responses.map((response, index) => (
                                                            <Accordion
                                                                key={index}
                                                                sx={{
                                                                    boxShadow: 'none',
                                                                    border: '1px solid #e2e8f0',
                                                                    borderRadius: 1,
                                                                    mb: 1,
                                                                    '&:before': { display: 'none' },
                                                                    '&.Mui-expanded': {
                                                                        backgroundColor: 'rgba(25, 118, 210, 0.04)',
                                                                        border: '1px solid rgba(25, 118, 210, 0.2)'
                                                                    }
                                                                }}
                                                            >
                                                                <AccordionSummary
                                                                    expandIcon={<ExpandMore />}
                                                                    sx={{
                                                                        minHeight: 48,
                                                                        '&.Mui-expanded': {
                                                                            minHeight: 48,
                                                                            backgroundColor: 'rgba(25, 118, 210, 0.06)'
                                                                        },
                                                                        '& .MuiAccordionSummary-content': {
                                                                            margin: '12px 0',
                                                                            '&.Mui-expanded': {
                                                                                margin: '12px 0'
                                                                            }
                                                                        }
                                                                    }}
                                                                >
                                                                    <Typography
                                                                        variant="body2"
                                                                        sx={{
                                                                            fontStyle: 'italic',
                                                                            color: 'text.primary',
                                                                            fontSize: '0.875rem',
                                                                            lineHeight: 1.4,
                                                                            display: '-webkit-box',
                                                                            WebkitLineClamp: 2,
                                                                            WebkitBoxOrient: 'vertical',
                                                                            overflow: 'hidden',
                                                                            textOverflow: 'ellipsis'
                                                                        }}
                                                                    >
                                                                        "{response.length > 120 ? response.substring(0, 120) + '...' : response}"
                                                                    </Typography>
                                                                </AccordionSummary>
                                                                <AccordionDetails
                                                                    sx={{
                                                                        backgroundColor: 'rgba(25, 118, 210, 0.02)',
                                                                        borderTop: '1px solid rgba(25, 118, 210, 0.1)',
                                                                        p: 2
                                                                    }}
                                                                >
                                                                    <Typography
                                                                        variant="body1"
                                                                        sx={{
                                                                            fontStyle: 'italic',
                                                                            lineHeight: 1.7,
                                                                            color: 'text.primary',
                                                                            fontSize: '0.95rem'
                                                                        }}
                                                                    >
                                                                        "{response}"
                                                                    </Typography>
                                                                </AccordionDetails>
                                                            </Accordion>
                                                        ))}
                                                    </Box>
                                                </Box>
                                            )}

                                            {/* Context Analysis */}
                                        </CardContent>
                                    </Card>
                                </Box>
                            )}
                        </Box>
                    ) : (
                        <Box>
                            {viewMode === "sentiment" ? (
                                <SentimentAnalysisView
                                    keywordsData={processedKeywordsData}
                                    statsData={statsData}
                                    selectedSentimentQuestion={selectedSentimentQuestion}
                                    setSelectedSentimentQuestion={setSelectedSentimentQuestion}
                                    reportId={reportId}
                                    onCellInsight={handleCellInsight}
                                />
                            ) : viewMode === "heatmap" ? (
                                <HeatmapView
                                    keywordsData={processedKeywordsData}
                                    selectedQuestion={selectedQuestion}
                                    setSelectedQuestion={setSelectedQuestion}
                                    sortedKeywords={sortedKeywords}
                                    reportId={reportId}
                                    onCellInsight={handleCellInsight}
                                />
                            ) : (
                                <Box>
                                    {/* Bar Chart View */}
                                    <Box
                                        sx={{
                                            p: 3,
                                            borderRadius: 3,
                                            background: 'rgba(255,255,255,0.95)',
                                            backdropFilter: 'blur(10px)',
                                            border: '1px solid rgba(255,255,255,0.2)',
                                            height: 400,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}
                                    >
                                        <MuiBarChart
                                            dataset={sortedKeywords.map((keyword, index) => ({
                                                word: keyword.word,
                                                frequency: keyword.frequency,
                                                color: getColor(index)
                                            }))}
                                            xAxis={[{
                                                scaleType: 'band',
                                                dataKey: 'word',
                                                tickLabelStyle: {
                                                    fontSize: 12,
                                                    fill: '#666',
                                                    angle: -45,
                                                    textAnchor: 'end'
                                                }
                                            }]}
                                            yAxis={[{
                                                tickLabelStyle: {
                                                    fontSize: 12,
                                                    fill: '#666'
                                                }
                                            }]}
                                            series={[{
                                                dataKey: 'frequency',
                                                color: '#1976d2',
                                                highlightScope: {
                                                    highlighted: 'item',
                                                    faded: 'global'
                                                }
                                            }]}
                                            width={800}
                                            height={350}
                                            slotProps={{
                                                legend: { hidden: true }
                                            }}
                                            margin={{ top: 20, bottom: 100, left: 60, right: 20 }}
                                            sx={{
                                                '& .MuiBarElement-root': {
                                                    filter: 'drop-shadow(0px 4px 6px rgba(25, 118, 210, 0.3))',
                                                    transition: 'all 0.3s ease',
                                                    '&:hover': {
                                                        filter: 'drop-shadow(0px 6px 12px rgba(25, 118, 210, 0.5))',
                                                    }
                                                }
                                            }}
                                        />
                                    </Box>
                                    {/* Stats Summary */}
                                    <Box sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        mt: 3,
                                        flexWrap: 'wrap',
                                        gap: 2
                                    }}>
                                        <Box sx={{ textAlign: 'center' }}>
                                            <Typography variant="h6" sx={{ color: 'black', fontWeight: 'bold' }}>
                                                {processedKeywordsData.length}
                                            </Typography>
                                            <Typography variant="caption" sx={{ color: 'rgba(0,0,0,0.6)' }}>
                                                <FormattedMessage id="keywordsSection.total_keywords" defaultMessage="Total Keywords" />
                                            </Typography>
                                        </Box>
                                        <Box sx={{ textAlign: 'center' }}>
                                            <Typography variant="h6" sx={{ color: 'black', fontWeight: 'bold' }}>
                                                {maxFrequency}
                                            </Typography>
                                            <Typography variant="caption" sx={{ color: 'rgba(0,0,0,0.6)' }}>
                                                <FormattedMessage id="keywordsSection.highest_frequency" defaultMessage="Highest Frequency" />
                                            </Typography>
                                        </Box>
                                        <Box sx={{ textAlign: 'center' }}>
                                            <Typography variant="h6" sx={{ color: 'black', fontWeight: 'bold' }}>
                                                {Math.round(processedKeywordsData.reduce((sum, k) => sum + k.frequency, 0) / processedKeywordsData.length)}
                                            </Typography>
                                            <Typography variant="caption" sx={{ color: 'rgba(0,0,0,0.6)' }}>
                                                <FormattedMessage id="keywordsSection.average_frequency" defaultMessage="Average Frequency" />
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Box>
                            )}
                        </Box>
                    )}
                </CardContent>
            </Card>
            <CellInsightDialog
                open={cellInsightOpen}
                onClose={() => setCellInsightOpen(false)}
                reportId={reportId}
                query={cellInsightQuery}
            />
        </Box >
    );
};

export default KeywordsSection;